import React from "react";
import { Box, Typography, Button, Paper, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import TuneIcon from "@mui/icons-material/Tune";

const NoResultsFound = ({
  searchQuery,
  hasActiveFilters,
  onClearFilters,
  isInitialState = false,
}) => {
  const theme = useTheme();

  // Show different content if this is the initial state (no search performed)
  if (isInitialState) {
    return (
      <Paper sx={{ p: 6, textAlign: "center", mt: 4 }}>
        <Box sx={{ mb: 3 }}>
          <SearchIcon
            sx={{
              fontSize: 64,
              color: theme.palette.primary.main,
              mb: 2,
            }}
          />
        </Box>

        <Typography variant="h5" component="h2" gutterBottom>
          Welcome to Advanced Athlete Search
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3, maxWidth: 600, mx: "auto" }}
        >
          Use the search bar above to find athletes by name, school, or
          location. Apply filters from the left panel to narrow down your search
          by physical attributes, academic performance, track times, and more.
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button variant="outlined" startIcon={<TuneIcon />} size="large">
            Use Filters to Get Started
          </Button>

          <Button variant="contained" startIcon={<SearchIcon />} size="large">
            Search Athletes
          </Button>
        </Box>

        <Box
          sx={{ mt: 4, p: 3, bgcolor: "background.default", borderRadius: 2 }}
        >
          <Typography variant="h6" gutterBottom>
            Quick Tips:
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "left", maxWidth: 500, mx: "auto" }}
          >
            • Use the search bar for names, schools, or locations
            <br />
            • Apply multiple filters to narrow your results
            <br />
            • Save your favorite searches for quick access
            <br />
            • Switch between card and list views
            <br />• Export your results for offline use
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Original no results found content
  return (
    <Paper sx={{ p: 6, textAlign: "center", mt: 4 }}>
      <Box sx={{ mb: 3 }}>
        <SearchIcon
          sx={{
            fontSize: 64,
            color: theme.palette.text.secondary,
            mb: 2,
          }}
        />
      </Box>

      <Typography variant="h5" component="h2" gutterBottom>
        No Athletes Found
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {searchQuery ? (
          <>
            No results found for "<strong>{searchQuery}</strong>". Try adjusting
            your search terms or filters.
          </>
        ) : (
          "No athletes match your current search criteria. Try adjusting your search terms or filters."
        )}
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {hasActiveFilters && (
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={onClearFilters}
          >
            Clear All Filters
          </Button>
        )}

        <Button variant="contained" startIcon={<SearchIcon />}>
          Try Different Search
        </Button>
      </Box>

      <Box sx={{ mt: 4, p: 3, bgcolor: "background.default", borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Suggestions:</strong>
          <br />
          • Check your spelling and try different keywords
          <br />
          • Remove some filters to broaden your search
          <br />
          • Try searching by position, school, or location
          <br />• Use the saved searches for popular queries
        </Typography>
      </Box>
    </Paper>
  );
};

export default NoResultsFound;
